import { isLikelyShipmentId, parseShipmentIdFromSearch } from './publicTrackRoute.js';

/**
 * Parse unit number from a QR payload (?u=2, /u/2, unit_no=2).
 */
export function parseUnitNoFromQrData(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;

  const queryMatch = text.match(/(?:^|[?&#])(?:u|unit|unit_no|parcel_unit)=([Uu]?\d+)/i);
  if (queryMatch?.[1]) {
    const digits = String(queryMatch[1]).replace(/^[Uu]+/, '');
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  const pathMatch = text.match(/\/u(?:nit)?\/(\d+)\b/i);
  if (pathMatch?.[1]) {
    const n = Number(pathMatch[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  try {
    const url = text.includes('://')
      ? new URL(text)
      : new URL(`https://local${text.startsWith('/') ? '' : '/'}${text}`);
    for (const key of ['u', 'unit', 'unit_no', 'parcel_unit']) {
      const value = url.searchParams.get(key);
      if (value) {
        const digits = String(value).replace(/^[Uu]+/, '');
        const n = Number(digits);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
  } catch {
    // fall through
  }

  return null;
}

/**
 * Parse shipment ID from a QR payload (tracking URL, query param, or plain ID).
 * Mirrors backend parse_shipment_id_from_qr_data.
 */
export function parseShipmentIdFromQrData(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';

  const lowered = text.toLowerCase();
  if (lowered.includes('shipment_id=') || lowered.includes('consignment_no=') || /(^|[?&])id=/.test(lowered)) {
    const match = text.match(/(?:shipment_id|consignment_no|(?:^|[?&])id)=([^&\s#]+)/i);
    if (match?.[1]) {
      const candidate = decodeURIComponent(match[1]).trim();
      if (isLikelyShipmentId(candidate)) return candidate;
    }
    try {
      const query = text.includes('?') ? text.split('?').slice(1).join('?') : text;
      const params = new URLSearchParams(query.startsWith('?') ? query : `?${query}`);
      for (const key of ['shipment_id', 'shipmentId', 'consignment_no', 'id']) {
        const value = params.get(key);
        if (value && isLikelyShipmentId(value)) return value.trim();
      }
    } catch {
      // fall through
    }
  }

  if (text.includes('://') || text.startsWith('/')) {
    try {
      const url = text.includes('://') ? new URL(text) : new URL(`https://local${text.startsWith('/') ? '' : '/'}${text}`);
      const parts = (url.pathname || '').split('/').filter(Boolean).map((p) => {
        try {
          return decodeURIComponent(p).trim();
        } catch {
          return p.trim();
        }
      });
      // Drop trailing /u/2 unit markers before reading the track key.
      if (
        parts.length >= 3
        && ['u', 'unit'].includes(String(parts[parts.length - 2] || '').toLowerCase())
        && /^\d+$/.test(parts[parts.length - 1] || '')
      ) {
        parts.pop();
        parts.pop();
      }
      const segment = parts[parts.length - 1] || '';
      if (isLikelyShipmentId(segment)) return segment;
      const fromQuery = parseShipmentIdFromSearch(url.searchParams);
      if (fromQuery) return fromQuery;
    } catch {
      // fall through
    }
  }

  if (isLikelyShipmentId(text)) return text;
  return '';
}

/** Full ref used by live scan / cart merge. */
export function parseShipmentUnitRefFromQrData(raw) {
  const shipmentId = parseShipmentIdFromQrData(raw);
  if (!shipmentId) {
    return { shipmentId: '', unitNo: null, legacy: true };
  }
  const unitNo = parseUnitNoFromQrData(raw);
  return {
    shipmentId,
    unitNo,
    legacy: unitNo == null,
  };
}

export function parseShipmentIdsFromQrPayloads(payloads) {
  const list = Array.isArray(payloads) ? payloads : [payloads];
  const seen = new Set();
  const ids = [];
  for (const payload of list) {
    const id = parseShipmentIdFromQrData(payload);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/** Build verified_units_by_shipment map from cart rows. */
export function buildVerifiedUnitsByShipment(shipments = []) {
  const out = {};
  for (const row of shipments) {
    const sid = String(row?.shipment_id || '').trim();
    if (!sid) continue;
    const units = Array.isArray(row?.scanned_unit_nos)
      ? row.scanned_unit_nos
      : (row?.scanned_unit?.unit_no != null ? [row.scanned_unit.unit_no] : []);
    const nos = [...new Set(
      units
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0),
    )].sort((a, b) => a - b);
    if (nos.length) out[sid] = nos;
  }
  return out;
}

export function isShipmentUnitScanComplete(shipment) {
  const expected = Number(shipment?.expected_units || shipment?.no_of_articles || 1) || 1;
  if (expected <= 1) return true;
  if (shipment?.allow_legacy_bypass) return true;
  const scanned = Array.isArray(shipment?.scanned_unit_nos) ? shipment.scanned_unit_nos.length : 0;
  return scanned >= expected;
}

export function shipmentUnitProgressLabel(shipment) {
  const expected = Number(shipment?.expected_units || shipment?.no_of_articles || 1) || 1;
  const scanned = Array.isArray(shipment?.scanned_unit_nos)
    ? shipment.scanned_unit_nos.length
    : (shipment?.scanned_unit ? 1 : 0);
  return `${scanned}/${expected}`;
}

export function shipmentMissingUnitLabels(shipment) {
  const units = Array.isArray(shipment?.units) ? shipment.units : [];
  const scanned = new Set(
    (shipment?.scanned_unit_nos || []).map((n) => Number(n)),
  );
  return units
    .filter((u) => !scanned.has(Number(u.unit_no)))
    .map((u) => `${u.article_type || 'PACKAGE'} (${u.unit_no}/${shipment.expected_units || units.length})`);
}
